import { trackEvent, AnalyticsEventProperties } from './posthog';
import { env } from '@/config/env';
import { Product, ProductVariant } from '@/types/api.types';

/**
 * Canonical names for every analytics event we capture.
 * Naming convention: `object_action` in snake_case.
 * See docs/ANALYTICS.md for the full taxonomy before adding new events.
 */
export const ANALYTICS_EVENTS = {
  userSignedUp: 'user_signed_up',
  userLoggedIn: 'user_logged_in',
  userLoggedOut: 'user_logged_out',
  sessionExpired: 'session_expired',
  productViewed: 'product_viewed',
  productAddedToCart: 'product_added_to_cart',
  productFavouriteToggled: 'product_favourite_toggled',
  productPublished: 'product_published',
  searchPerformed: 'search_performed',
  checkoutStarted: 'checkout_started',
  orderPlaced: 'order_placed',
  paymentSucceeded: 'payment_succeeded',
  paymentFailed: 'payment_failed',
  pageEngagement: 'page_engagement',
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/** Same client shape accepted by `trackEvent` (overridable for tests). */
type TrackClient = Parameters<typeof trackEvent>[2];

/** Every event carries the environment so dev/prod data can be filtered in one project. */
const withAppEnv = (properties: AnalyticsEventProperties) => ({
  app_env: env.appEnv,
  ...properties,
});

export const trackUserSignedUp = (role: string, client?: TrackClient) => {
  trackEvent(
    ANALYTICS_EVENTS.userSignedUp,
    withAppEnv({ role }),
    client
  );
};

export const trackUserLoggedIn = (role: string, client?: TrackClient) => {
  trackEvent(
    ANALYTICS_EVENTS.userLoggedIn,
    withAppEnv({ role, login_method: 'manual' }),
    client
  );
};

export const trackUserLoggedOut = (method: string = 'manual', client?: TrackClient) => {
  trackEvent(
    ANALYTICS_EVENTS.userLoggedOut,
    withAppEnv({ method }),
    client
  );
};

export const trackSessionExpired = (reason: string, client?: TrackClient) => {
  trackEvent(
    ANALYTICS_EVENTS.sessionExpired,
    withAppEnv({ reason }),
    client
  );
};

export const trackSearchPerformed = (
  query: string,
  source:
    | 'market_header'
    | 'market_header_mobile'
    | 'unified_header'
    | 'store_hero'
    | 'results_page',
  client?: TrackClient
) => {
  const trimmed = query.trim().slice(0, 100);
  trackEvent(
    ANALYTICS_EVENTS.searchPerformed,
    withAppEnv({
      query: trimmed,
      query_length: trimmed.length,
      source,
    }),
    client
  );
};

export const trackProductFavouriteToggled = (
  productId: string,
  favourited: boolean,
  client?: TrackClient
) => {
  trackEvent(
    ANALYTICS_EVENTS.productFavouriteToggled,
    withAppEnv({
      product_id: productId,
      favourited,
    }),
    client
  );
};

export const trackProductPublished = (
  product: { productId: string; productName: string; role: 'farmer' | 'supplier'; isEdit: boolean },
  client?: TrackClient
) => {
  trackEvent(
    ANALYTICS_EVENTS.productPublished,
    withAppEnv({
      product_id: product.productId,
      product_name: product.productName,
      role: product.role,
      is_edit: product.isEdit,
    }),
    client
  );
};

export interface PageEngagementProperties extends AnalyticsEventProperties {
  path: string;
  duration_ms: number;
  active_ms: number;
  idle_ms: number;
  flush_reason?: string;
}

export const trackPageEngagement = (
  properties: PageEngagementProperties,
  client?: TrackClient
) => {
  trackEvent(ANALYTICS_EVENTS.pageEngagement, withAppEnv(properties), client);
};

export const trackProductViewed = (
  product: Pick<Product, 'id' | 'name' | 'price' | 'category_id' | 'category' | 'farmer_id' | 'supplier_id'>,
  client?: TrackClient
) => {
  trackEvent(
    ANALYTICS_EVENTS.productViewed,
    withAppEnv({
      product_id: product.id,
      product_name: product.name,
      price: parseFloat(product.price),
      category_id: product.category_id,
      category_name: product.category?.name,
      seller_id: product.supplier_id || product.farmer_id,
      seller_type: product.supplier_id ? 'supplier' : 'farmer',
    }),
    client
  );
};

export const trackProductAddedToCart = (
  product: Pick<Product, 'id' | 'name' | 'price' | 'farmer_id' | 'supplier_id'>,
  quantity: number,
  variant?: ProductVariant | null,
  client?: TrackClient
) => {
  const price = variant ? parseFloat(variant.price) : parseFloat(product.price);

  trackEvent(
    ANALYTICS_EVENTS.productAddedToCart,
    withAppEnv({
      product_id: product.id,
      product_name: product.name,
      variant_id: variant?.id,
      price,
      quantity,
      seller_id: product.supplier_id || product.farmer_id,
      seller_type: product.supplier_id ? 'supplier' : 'farmer',
    }),
    client
  );
};

export const trackCheckoutStarted = (
  itemCount: number,
  cartValue: number,
  packageCount: number,
  client?: TrackClient
) => {
  trackEvent(
    ANALYTICS_EVENTS.checkoutStarted,
    withAppEnv({
      item_count: itemCount,
      cart_value: cartValue,
      package_count: packageCount,
    }),
    client
  );
};

export const trackOrderPlaced = (
  orderIds: string[],
  totalValue: number,
  fulfillment: { deliveryPackages: number; pickupPackages: number },
  client?: TrackClient
) => {
  trackEvent(
    ANALYTICS_EVENTS.orderPlaced,
    withAppEnv({
      order_ids: orderIds,
      order_count: orderIds.length,
      total_value: totalValue,
      delivery_packages: fulfillment.deliveryPackages,
      pickup_packages: fulfillment.pickupPackages,
    }),
    client
  );
};

export const trackPaymentSucceeded = (
  reference: string,
  orderId?: string | null,
  client?: TrackClient
) => {
  trackEvent(
    ANALYTICS_EVENTS.paymentSucceeded,
    withAppEnv({
      payment_reference: reference,
      order_id: orderId ?? undefined,
    }),
    client
  );
};

export const trackPaymentFailed = (
  stage: 'initialization' | 'verification',
  details: { reference?: string; orderId?: string | null; error?: string } = {},
  client?: TrackClient
) => {
  trackEvent(
    ANALYTICS_EVENTS.paymentFailed,
    withAppEnv({
      stage,
      payment_reference: details.reference,
      order_id: details.orderId ?? undefined,
      error: details.error,
    }),
    client
  );
};
